import { useEffect, useState } from 'react';
import type { HostSpec, MailboxProviderId } from 'fym-shared/src/music24/outreach/mailboxProviders';
import { useTRPCAPI, useTRPCQuery } from '../../../../trpc/trpc';

/** `outreach.mailboxes.detectProvider` (music24-api `DetectedProvider`). */
export interface DetectedProvider {
  providerId: MailboxProviderId;
  imap: HostSpec | null;
  smtp: HostSpec | null;
}

/** The wait after the last key before the lookup (the server may read the MX hosts, up to 3 s). */
export const DETECT_DEBOUNCE_MS = 400;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** The mailbox kind of a typed address (design outreach-v2 o1): the known domains, then the MX hosts. */
export function useDetectMailboxProvider(email: string) {
  const trpcAPI = useTRPCAPI();
  const [settled, setSettled] = useState(email.trim());
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(email.trim()), DETECT_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [email]);
  const enabled = EMAIL_PATTERN.test(settled);
  const query = useTRPCQuery(
    trpcAPI.outreach.mailboxes.detectProvider.queryOptions(
      { email: settled },
      { enabled, retry: false, staleTime: Infinity }
    )
  );
  return {
    detected: enabled ? ((query.data as DetectedProvider | null | undefined) ?? null) : null,
    isDetecting: enabled && query.isLoading,
  };
}
